import { Image, X } from "lucide-react";
import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
import { ErrorMessage } from "formik";
import imageCompression from "browser-image-compression";
import { useTranslation } from "react-i18next";
import { formatFileTypes, handleStopPropagation } from "utils/utils";
import { showAlert } from "store/slices/alertSlice";

const FileUpload = ({
  uploadedFiles,
  setUploadedFiles,
  maxFiles = 3,
  maxFileSize = 5 * 1024 * 1024, // 5 MB
  acceptedFileTypes = ["image/png", "image/jpeg"],
  onChange = () => {},
  className = "",
  isShowErrorMsg = false,
  name = "",
  maxUploadSize = 50 * 1024 * 1024,
  design2 = false,
  multiple = true,
  label = null,
  required = null,
}) => {
  const dispatch = useDispatch();
  const [compressionProgress, setCompressionProgress] = useState(0);
  const { t } = useTranslation();

  // Normalize uploadedFiles to always work with array internally
  const filesArray = multiple
    ? Array.isArray(uploadedFiles)
      ? uploadedFiles
      : uploadedFiles
        ? [uploadedFiles]
        : []
    : uploadedFiles
      ? [uploadedFiles]
      : [];

  const formatAcceptedTypes = () => {
    const result = {};
    acceptedFileTypes?.forEach((type) => {
      result[type] = [];
    });
    return result;
  };

  const compressImage = async (file) => {
    if (file.size <= maxFileSize) {
      return file; // ✅ No compression needed
    }

    const options = {
      maxSizeMB: maxFileSize / (1024 * 1024),
      useWebWorker: false,
      maxWidthOrHeight: 2048,
      onProgress: (percent) => {
        setCompressionProgress(percent);
      },
    };

    const compressedFile = await imageCompression(file, options);

    const renamedFile = new File([compressedFile], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    });
    setCompressionProgress(0); // reset after complete
    return renamedFile;
  };

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      rejectedFiles.forEach(({ file, errors }) => {
        const formattedErrors = errors
          .map((e) => {
            if (e.code === "file-too-large") {
              return t("File is larger than {{size}} MB", {
                size: (maxUploadSize / (1024 * 1024)).toFixed(1),
              });
            }
            return e.message;
          })
          .join(", ");

        dispatch(
          showAlert({
            text: t("File rejected: {{fileName}} ({{errors}})", {
              fileName: file.name,
              errors: formattedErrors,
            }),
            type: "error",
          }),
        );
      });

      const newFiles = [];

      for (const file of acceptedFiles) {
        try {
          // Compress if needed
          const processedFile = await compressImage(file);
          newFiles.push(processedFile);
        } catch (error) {
          dispatch(
            showAlert({
              text: t("File rejected: {{fileName}} ({{error}})", {
                fileName: file.name,
                error: error?.message || error,
              }),
              type: "error",
            }),
          );
        }
      }

      // For single file mode, replace existing file
      if (!multiple) {
        if (newFiles.length > 0) {
          const singleFile = newFiles[0];
          setUploadedFiles(singleFile); // Set as single file, not array
          onChange(singleFile);
        }
        return;
      }

      // For multiple file mode, check max limit
      if (filesArray.length + newFiles.length > maxFiles) {
        dispatch(
          showAlert({
            text: t("You can only upload a maximum of {{maxFiles}} images.", {
              maxFiles,
            }),
            type: "error",
          }),
        );
        return;
      }

      const updatedFiles = [...filesArray, ...newFiles];
      setUploadedFiles(updatedFiles);
      onChange(
        updatedFiles.filter((f) => f instanceof File || f instanceof Blob),
      );
    },
    [filesArray, maxFiles, multiple, onChange, dispatch, t, maxUploadSize],
  );

  const removeFile = (index) => {
    if (!multiple) {
      // For single file mode, clear the file
      setUploadedFiles(null);
      onChange(null);
    } else {
      // For multiple file mode, remove specific file
      const updatedFiles = filesArray.filter((_, i) => i !== index);
      setUploadedFiles(updatedFiles);
      onChange(
        updatedFiles.filter((f) => f instanceof File || f instanceof Blob),
      );
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: formatAcceptedTypes(),
    maxSize: maxUploadSize,
    multiple: multiple,
  });

  return (
    <div className="my-3">
      {/* Drag-and-Drop Area */}
      {label && (
        <label htmlFor="" className="file_upload_label">
          {label}
          {required && <span className="text-danger">*</span>}
        </label>
      )}

      <div
        {...getRootProps()}
        className={`${design2 ? "" : "file_upload"} ${className}`}
        style={{
          background: isDragActive ? "#f0f0f0" : "#ffffff",
        }}
      >
        {/* Compression Progress */}
        {compressionProgress > 0 && (
          <div className="compression-overlay">
            <div className="compression-box">
              <div className="compression-text">
                {t("Compressing image... {{progress}}%", {
                  progress: Math.round(compressionProgress),
                })}
              </div>

              <div className="compression-progress">
                <div
                  className="compression-progress-bar"
                  style={{ width: `${compressionProgress}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}
        <div className="g_between">
          <input {...getInputProps()} />
        </div>

        {/* Display Uploaded Images */}
        <div>
          {!filesArray?.length ? (
            <div className="d-flex flex-column w-100 align-items-center">
              <Image />
              <p className="w-100 text_black fs_14">
                <span>{t("Choose a file")}</span> {t("or drag & drop it here")}
                <br />
                {multiple && (
                  <>
                    ({t("Max")} {maxFiles} {t("files")})
                  </>
                )}
              </p>
              <p className="fs_12 text_dark_gray_2 mt-2">
                {formatFileTypes(acceptedFileTypes)}
              </p>
            </div>
          ) : (
            <div className="d-flex align-items-end flex-wrap gap-4">
              <div className="uploaded_images">
                {filesArray?.map((item, index) => {
                  let previewUrl = "";
                  if (item instanceof File || item instanceof Blob) {
                    previewUrl = URL.createObjectURL(item);
                  } else if (typeof item === "string") {
                    previewUrl = item;
                  }

                  const isVideoFile =
                    item instanceof File || item instanceof Blob
                      ? item.type?.startsWith("video")
                      : typeof item === "string" &&
                        /\.(mp4|mov|webm|avi|mkv)$/i.test(item);

                  return (
                    <div
                      key={index}
                      className="position-relative single_img"
                      onClick={handleStopPropagation}
                    >
                      {isVideoFile ? (
                        <video
                          src={previewUrl}
                          controls
                          className="uploaded_image"
                        />
                      ) : (
                        <img
                          src={previewUrl}
                          alt={t("Uploaded Preview {{index}}", {
                            index: index + 1,
                          })}
                          className="uploaded_image"
                        />
                      )}
                      <X
                        className="trash_icon"
                        onClick={() => removeFile(index)}
                      />
                    </div>
                  );
                })}
                {multiple && filesArray.length !== maxFiles && (
                  <button type="button" className="upload_btn">
                    +
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {isShowErrorMsg && (
        <ErrorMessage name={name} component="div" className="text-danger" />
      )}
    </div>
  );
};

export default FileUpload;
